// AdminRatingsPage.tsx - Complete with Case Management
import React, { useState, useEffect } from 'react';
import { useToast } from './Toast';

import { tc, useLegible } from "./theme/legible";
const BACKEND_URL = '';

const RATING_EMOJIS: { [key: number]: string } = {
  1: '😠', 2: '😟', 3: '😐', 4: '🙂', 5: '😊',
};

const CLASS_COLORS: Record<string, string> = {
  'warrior': '#C79C6E', 'paladin': '#F58CBA', 'hunter': '#ABD473', 'rogue': '#FFF569',
  'priest': '#FFFFFF', 'death knight': '#C41F3B', 'shaman': '#0070DE', 'mage': '#69CCF0',
  'warlock': '#9482C9', 'monk': '#00FF96', 'druid': '#FF7D0A', 'demon hunter': '#A330C9',
};

// Types
interface FlaggedCharacter {
  case_id: string;
  character_id: string;
  character_name: string;
  class_name: string;
  user_display_name: string;
  case_type: string;
  severity: string;
  status: string;
  ratings_count_at_flag: number;
  flagged_at: string;
  current_total_ratings: number;
  current_overall_rating: number;
  current_poor_conduct: number;
  current_poor_skill: number;
  current_poor_timeliness: number;
  current_poor_preparation: number;
  identity_concerns_count: number;
  new_ratings_since_flag: number;
  has_new_violations: boolean;
}

interface CharacterSearchResult {
  id: string;
  name: string;
  class_name: string;
  user_display_name: string;
  total_ratings: number;
  overall_rating: number;
  identity_concerns_count: number;
}

interface RatingSummary {
  character_id: string;
  character_name: string;
  class_name: string;
  user_display_name: string;
  total_ratings: number;
  avg_conduct: number;
  avg_skill: number;
  avg_timeliness: number;
  avg_preparation: number;
  overall_rating: number;
  conduct_rating_count: number;
  skill_rating_count: number;
  timeliness_rating_count: number;
  preparation_rating_count: number;
  rating_distribution: {
    conduct: { [key: string]: number };
    skill: { [key: string]: number };
    timeliness: { [key: string]: number };
    preparation: { [key: string]: number };
  };
  identity_concerns_count: number;
  poor_conduct_count: number;
  poor_skill_count: number;
  poor_timeliness_count: number;
  poor_preparation_count: number;
  notes_count: number;
  evidence_count: number;
  last_rated_at: string;
  ratings_last_7_days: number;
  ratings_last_30_days: number;
}

interface RatingDetail {
  id: string;
  conduct_rating: number | null;
  skill_rating: number | null;
  timeliness_rating: number | null;
  preparation_rating: number | null;
  identity_concern: boolean;
  identity_concern_details: string | null;
  notes: string | null;
  evidence_urls: string[] | null;
  created_at: string;
  rater_character_name: string;
  rater_class_name: string;
  rater_user_name: string;
  raid_name: string;
  raid_instance: string;
  raid_date: string;
  has_poor_ratings: boolean;
  has_notes: boolean;
  has_evidence: boolean;
}

interface CaseDetail {
  id: string;
  case_type: string;
  severity: string;
  status: string;
  admin_notes: string | null;
  action_taken: string | null;
  resolution_notes: string | null;
  created_at: string;
  resolved_at: string | null;
}

const getClassColor = (className: string | null) => {
  if (!className) return '#6366F1';
  return CLASS_COLORS[className.toLowerCase()] || '#6366F1';
};

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'critical': return 'bg-red-500/20 border-red-500/50 text-red-700 dark:text-red-300';
    case 'high': return 'bg-orange-500/20 border-orange-500/50 text-orange-700 dark:text-orange-300';
    case 'medium': return 'bg-yellow-500/20 border-yellow-500/50 text-yellow-700 dark:text-yellow-300';
    default: return 'bg-blue-500/20 border-blue-500/50 text-blue-700 dark:text-blue-300';
  }
};

const getCaseTypeLabel = (caseType: string) => {
  const labels: Record<string, string> = {
    poor_conduct: '⚠️ Poor Conduct',
    poor_skill: '📉 Poor Skill',
    poor_timeliness: '⏰ Timeliness',
    poor_preparation: '🎒 Preparation',
    identity_concern: '🚨 Identity',
    general: '📋 General',
  };
  return labels[caseType] || caseType;
};

const RatingDistributionBar = ({ 
  category, 
  distribution 
}: { 
  category: string; 
  distribution: { [key: string]: number }; 
}) => {
  const total = Object.values(distribution).reduce((sum, count) => sum + count, 0);
  if (total === 0) return null;

  return (
    <div className="space-y-1">
      <div className="text-xs font-bold text-[var(--c-ink-2)] uppercase tracking-wider">{category}</div>
      <div className="flex items-center gap-1 h-8">
        {[1, 2, 3, 4, 5].map(rating => {
          const count = distribution[rating] || 0;
          const percentage = total > 0 ? (count / total) * 100 : 0;
          
          if (count === 0) return null;
          
          return (
            <div
              key={rating}
              className="relative h-full flex items-center justify-center text-xs font-bold transition-all hover:scale-110"
              style={{ 
                width: `${percentage}%`,
                backgroundColor: rating <= 2 ? '#EF4444' : rating === 3 ? '#F59E0B' : '#10B981',
                minWidth: count > 0 ? '32px' : '0'
              }}
              title={`${RATING_EMOJIS[rating]} - ${count} rating${count !== 1 ? 's' : ''}`}
            >
              <span className="text-[var(--c-ink)] drop-shadow">{RATING_EMOJIS[rating]}</span>
              <span className="ml-1 text-[var(--c-ink)] text-[10px]">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function AdminRatingsPage() {
  useLegible();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'flagged' | 'search' | 'history'>('flagged');
  
  // Flagged characters state
  const [flaggedChars, setFlaggedChars] = useState<FlaggedCharacter[]>([]);
  const [flaggedLoading, setFlaggedLoading] = useState(false);
  const [checkingFlags, setCheckingFlags] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CharacterSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  
  // Selected character state
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterSearchResult | FlaggedCharacter | null>(null);
  const [summary, setSummary] = useState<RatingSummary | null>(null);
  const [ratings, setRatings] = useState<RatingDetail[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Case management state
  const [activeCase, setActiveCase] = useState<CaseDetail | null>(null);
  const [caseEvidence, setCaseEvidence] = useState<RatingDetail[]>([]);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolvingCase, setResolvingCase] = useState(false);

  // Ban management state
  const [userBanStatus, setUserBanStatus] = useState<{ userId: string; isBanned: boolean; displayName: string } | null>(null);
  const [banLoading, setBanLoading] = useState(false);


  // Load flagged characters
  const loadFlaggedCharacters = async () => {
    setFlaggedLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/admin/cases/flagged`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setFlaggedChars(data);
      }
    } catch (e) {
      console.error('Failed to load flagged characters:', e);
    } finally {
      setFlaggedLoading(false);
    }
  };

  // Check for new flags
  const checkForNewFlags = async () => {
    setCheckingFlags(true);
    try {
      const res = await fetch(`${BACKEND_URL}/admin/cases/check-flags`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.newFlags === 0) {
          showToast('No new flags found', 'success');
        } else {
          showToast(`Found ${data.newFlags} new flag${data.newFlags !== 1 ? 's' : ''}!`, 'info');
        }
        loadFlaggedCharacters();
      }
    } catch (e) {
      console.error('Failed to check flags:', e);
      showToast('Failed to check for flags', 'error');
    } finally {
      setCheckingFlags(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      performSearch();
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const performSearch = async () => {
    setSearching(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/admin/character-ratings/search?q=${encodeURIComponent(searchQuery)}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data);
      }
    } catch (e) {
      console.error('Search failed:', e);
    } finally {
      setSearching(false);
    }
  };

  const handleBanToggle = async () => {
    if (!userBanStatus) return;
    const action = userBanStatus.isBanned ? 'unban' : 'ban';
    if (!window.confirm(`Are you sure you want to ${action} ${userBanStatus.displayName}? ${!userBanStatus.isBanned ? 'They will be immediately logged out and unable to access the platform.' : ''}`)) return;
    
    setBanLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/admin/users/${userBanStatus.userId}/ban`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ban: !userBanStatus.isBanned })
      });
      if (res.ok) {
        const data = await res.json();
        setUserBanStatus({ userId: data.id, isBanned: data.is_banned, displayName: data.display_name });
        showToast(`User ${data.is_banned ? 'banned' : 'unbanned'} successfully`, data.is_banned ? 'error' : 'success');
      } else {
        const err = await res.json();
        showToast(err.error || 'Failed to update ban status', 'error');
      }
    } catch (e) {
      showToast('Failed to update ban status', 'error');
    } finally {
      setBanLoading(false);
    }
  };

  const loadCharacterDetails = async (char: CharacterSearchResult | FlaggedCharacter) => {
    setSelectedCharacter(char);
    setLoading(true);
    setSummary(null);
    setRatings([]);
    setActiveCase(null);
    setUserBanStatus(null);
    setCaseEvidence([]);

    const charId = 'character_id' in char ? char.character_id : char.id;

    try {
      // Load ratings summary
      const res = await fetch(
        `${BACKEND_URL}/admin/character-ratings/${charId}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const data = await res.json();
        setSummary(data.summary);
        setRatings(data.ratings);
        
        // Look up ban status by user_id directly
        if (data.summary?.user_id) {
          try {
            const banRes = await fetch(`${BACKEND_URL}/admin/users/${data.summary.user_id}/ban-status`, { credentials: 'include' });
            if (banRes.ok) {
              const banData = await banRes.json();
              setUserBanStatus({ userId: banData.id, isBanned: !!banData.is_banned, displayName: banData.display_name });
            }
          } catch (e) { console.error('Failed to look up ban status:', e); }
        }
      }

      // If this is a flagged character, load case details
      if ('case_id' in char) {
        const caseRes = await fetch(
          `${BACKEND_URL}/admin/cases/${char.case_id}`,
          { credentials: 'include' }
        );
        if (caseRes.ok) {
          const caseData = await caseRes.json();
          setActiveCase(caseData.case);
          setCaseEvidence(caseData.evidence);
          setAdminNotes(caseData.case.admin_notes || '');
          setActionTaken(caseData.case.action_taken || '');
          setResolutionNotes(caseData.case.resolution_notes || '');
        }
      }
    } catch (e) {
      console.error('Failed to load details:', e);
    } finally {
      setLoading(false);
    }
  };

  const updateCaseNotes = async () => {
    if (!activeCase) return;

    try {
      const res = await fetch(`${BACKEND_URL}/admin/cases/${activeCase.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ admin_notes: adminNotes })
      });
      if (res.ok) {
        showToast('Notes saved', 'success');
      }
    } catch (e) {
      console.error('Failed to save notes:', e);
      showToast('Failed to save notes', 'error');
    }
  };

  const resolveCase = async () => {
    if (!activeCase || !actionTaken) {
      showToast('Please specify what action was taken', 'error');
      return;
    }

    setResolvingCase(true);
    try {
      const res = await fetch(`${BACKEND_URL}/admin/cases/${activeCase.id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action_taken: actionTaken,
          resolution_notes: resolutionNotes
        })
      });
      if (res.ok) {
        showToast('Case resolved successfully', 'success');
        setSelectedCharacter(null);
        loadFlaggedCharacters();
      }
    } catch (e) {
      console.error('Failed to resolve case:', e);
      showToast('Failed to resolve case', 'error');
    } finally {
      setResolvingCase(false);
    }
  };

  const dismissCase = async () => {
    if (!activeCase) return;
    if (!window.confirm('Are you sure you want to dismiss this case?')) return;

    try {
      const res = await fetch(`${BACKEND_URL}/admin/cases/${activeCase.id}/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: resolutionNotes || 'Not an issue' })
      });
      if (res.ok) {
        showToast('Case dismissed', 'success');
        setSelectedCharacter(null);
        loadFlaggedCharacters();
      }
    } catch (e) {
      console.error('Failed to dismiss case:', e);
      showToast('Failed to dismiss case', 'error');
    }
  };

  // Load flagged on mount
  useEffect(() => {
    if (activeTab === 'flagged') {
      loadFlaggedCharacters();
    }
  }, [activeTab]);

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black text-[var(--c-ink)] mb-2">Player Reports Dashboard</h1>
          <p className="text-[var(--c-ink-2)]">Manage flagged players and view rating history</p>
        </div>

        {/* Tab Switcher */}
        <div className="mb-6 flex gap-2">
          <button
            onClick={() => setActiveTab('flagged')}
            className={`px-6 py-3 rounded-lg font-bold transition-all ${
              activeTab === 'flagged'
                ? 'bg-red-500/30 border-2 border-red-500/50 text-red-800 dark:text-red-200'
                : 'bg-[var(--c-sunken)] border-2 border-[var(--c-line)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)]'
            }`}
          >
            🚨 Flagged ({flaggedChars.length})
          </button>
          <button
            onClick={() => setActiveTab('search')}
            className={`px-6 py-3 rounded-lg font-bold transition-all ${
              activeTab === 'search'
                ? 'bg-cyan-500/30 border-2 border-cyan-500/50 text-cyan-800 dark:text-cyan-200'
                : 'bg-[var(--c-sunken)] border-2 border-[var(--c-line)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)]'
            }`}
          >
            🔍 Search
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-6 py-3 rounded-lg font-bold transition-all ${
              activeTab === 'history'
                ? 'bg-purple-500/30 border-2 border-purple-500/50 text-purple-800 dark:text-purple-200'
                : 'bg-[var(--c-sunken)] border-2 border-[var(--c-line)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)]'
            }`}
          >
            📚 History
          </button>
        </div>

        {/* FLAGGED TAB */}
        {activeTab === 'flagged' && (
          <div className="space-y-6">
            {/* Alert Banner */}
            {flaggedChars.length > 0 && (
              <div className="bg-red-500/20 border-2 border-red-500/50 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-black text-red-800 dark:text-red-200 mb-1">
                      ⚠️ {flaggedChars.length} Player{flaggedChars.length !== 1 ? 's' : ''} Flagged
                    </div>
                    <div className="text-red-700/80 dark:text-red-300/80">Repeat offenders requiring attention</div>
                  </div>
                  <button
                    onClick={checkForNewFlags}
                    disabled={checkingFlags}
                    className="px-6 py-3 rounded-lg bg-red-500/30 border border-red-500/50 text-red-800 dark:text-red-200 font-bold hover:bg-red-500/40 disabled:opacity-50 transition-all"
                  >
                    {checkingFlags ? 'Checking...' : '🔄 Check for New Flags'}
                  </button>
                </div>
              </div>
            )}

            {/* Flagged Characters Grid */}
            {flaggedLoading ? (
              <div className="text-center py-20 text-[var(--c-ink-2)]">Loading flagged characters...</div>
            ) : flaggedChars.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-6xl mb-4">✅</div>
                <div className="text-xl font-bold text-[var(--c-ink)] mb-2">No Flagged Players</div>
                <div className="text-[var(--c-ink-3)] mb-6">All clear! Click "Check for New Flags" to scan for issues.</div>
                <button
                  onClick={checkForNewFlags}
                  disabled={checkingFlags}
                  className="px-6 py-3 rounded-lg bg-cyan-500/30 border border-cyan-500/50 text-cyan-800 dark:text-cyan-200 font-bold hover:bg-cyan-500/40 disabled:opacity-50"
                >
                  {checkingFlags ? 'Checking...' : '🔄 Check for New Flags'}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {flaggedChars.map(char => (
                  <div
                    key={char.case_id}
                    className={`rounded-xl border-2 p-5 cursor-pointer hover:scale-[1.02] transition-all ${getSeverityColor(char.severity)}`}
                    onClick={() => loadCharacterDetails(char)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center text-[var(--c-ink)] font-bold"
                          style={{ backgroundColor: getClassColor(char.class_name) }}
                        >
                          {char.character_name.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div 
                            className="font-bold text-lg"
                            style={{ color: tc(getClassColor(char.class_name)) }}
                          >
                            {char.character_name}
                          </div>
                          <div className="text-xs opacity-80">{char.user_display_name}</div>
                        </div>
                      </div>
                      <div className="text-xs font-bold uppercase px-2 py-1 rounded bg-[var(--c-surface)]">
                        {char.severity}
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="text-sm font-bold mb-1">{getCaseTypeLabel(char.case_type)}</div>
                      <div className="text-xs opacity-80">
                        Flagged {new Date(char.flagged_at).toLocaleDateString()} 
                        {char.new_ratings_since_flag > 0 && ` • ${char.new_ratings_since_flag} new ratings since`}
                      </div>
                    </div>

                    {char.has_new_violations && (
                      <div className="px-3 py-2 rounded-lg bg-red-500/30 border border-red-500/50 text-red-800 dark:text-red-200 text-sm font-bold">
                        ⚠️ New violations since flagged
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SEARCH TAB */}
        {activeTab === 'search' && (
          <div className="space-y-6">
            {/* Search Bar */}
            <div className="relative">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--c-ink-3)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by character name or player name..."
                className="w-full pl-12 pr-4 py-4 rounded-xl border border-[var(--c-line-strong)] bg-[var(--c-surface)] text-[var(--c-ink)] placeholder:text-[var(--c-ink-3)] outline-none focus:border-cyan-500/50 focus:bg-[var(--c-surface)] focus:ring-2 focus:ring-cyan-500/20 text-lg"
              />
              {searching && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="w-5 h-5 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
                </div>
              )}
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="rounded-xl border border-[var(--c-line-strong)] bg-[var(--c-surface)] overflow-hidden">
                {searchResults.map(char => (
                  <button
                    key={char.id}
                    onClick={() => loadCharacterDetails(char)}
                    className="w-full p-4 hover:bg-[var(--c-sunken)] border-b border-[var(--c-line)] last:border-b-0 text-left transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center text-[var(--c-ink)] font-bold text-sm"
                          style={{ backgroundColor: getClassColor(char.class_name) }}
                        >
                          {char.name.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div 
                            className="font-bold text-lg"
                            style={{ color: tc(getClassColor(char.class_name)) }}
                          >
                            {char.name}
                          </div>
                          <div className="text-sm text-[var(--c-ink-2)]">
                            {char.class_name} • {char.user_display_name}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {char.total_ratings > 0 && (
                          <div className="text-right">
                            <div className="text-2xl font-bold text-[var(--c-ink)]">{Number(char.overall_rating || 0).toFixed(1)}</div>
                            <div className="text-xs text-[var(--c-ink-3)]">{char.total_ratings} ratings</div>
                          </div>
                        )}
                        {char.identity_concerns_count > 0 && (
                          <div className="px-3 py-1 rounded-full bg-red-500/20 border border-red-500/40 text-red-700 dark:text-red-300 text-xs font-bold">
                            ⚠️ {char.identity_concerns_count}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {!selectedCharacter && searchQuery.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 rounded-full bg-[var(--c-sunken)] flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-[var(--c-ink-3)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-[var(--c-ink)] mb-2">Search for a Player</h3>
                <p className="text-[var(--c-ink-3)]">Enter a character or player name to view their ratings</p>
              </div>
            )}
          </div>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <div className="text-center py-20 text-[var(--c-ink-2)]">
            History tab - Coming soon
          </div>
        )}

        {/* CHARACTER DETAIL MODAL */}
        {selectedCharacter && summary && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="relative bg-[var(--c-surface)] rounded-xl border border-[var(--c-line-strong)] max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl my-8">
              
              {/* Close Button */}
              <button
                onClick={() => setSelectedCharacter(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-lg hover:bg-[var(--c-sunken-2)] flex items-center justify-center transition-colors"
              >
                <svg className="w-6 h-6 text-[var(--c-ink-2)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="overflow-y-auto max-h-[90vh]">
                <div className="p-6 space-y-6">
                  
                  {/* Character Header */}
                  <div className="bg-gradient-to-br from-[var(--c-surface)] to-[var(--c-surface)] border border-[var(--c-line-strong)] rounded-xl p-6">
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-20 h-20 rounded-xl flex items-center justify-center text-[var(--c-ink)] font-bold text-2xl shadow-lg"
                        style={{ backgroundColor: getClassColor(summary.class_name) }}
                      >
                        {'name' in selectedCharacter ? selectedCharacter.name.substring(0, 2).toUpperCase() : selectedCharacter.character_name.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div 
                            className="text-3xl font-black"
                            style={{ color: tc(getClassColor(summary.class_name)) }}
                          >
                            {summary.character_name}
                          </div>
                          {userBanStatus?.isBanned && (
                            <span className="px-3 py-1 rounded-full bg-red-500/30 border border-red-500/50 text-red-700 dark:text-red-300 text-xs font-bold uppercase">Banned</span>
                          )}
                        </div>
                        <div className="text-[var(--c-ink-2)]">{summary.class_name} &bull; {summary.user_display_name}</div>
                      </div>
                      {userBanStatus && (
                        <button
                          onClick={handleBanToggle}
                          disabled={banLoading}
                          className={`px-5 py-2.5 rounded-lg font-bold text-sm transition-all disabled:opacity-50 ${
                            userBanStatus.isBanned
                              ? 'bg-green-500/20 border border-green-500/40 text-green-700 dark:text-green-300 hover:bg-green-500/30'
                              : 'bg-red-500/20 border border-red-500/40 text-red-700 dark:text-red-300 hover:bg-red-500/30'
                          }`}
                        >
                          {banLoading ? '...' : userBanStatus.isBanned ? 'Unban User' : 'Ban User'}
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                      <div className="bg-[var(--c-sunken)] rounded-lg p-4 border border-[var(--c-line)]">
                        <div className="text-3xl font-black text-[var(--c-ink)]">{summary.total_ratings}</div>
                        <div className="text-xs text-[var(--c-ink-2)] uppercase tracking-wider">Total Ratings</div>
                      </div>
                      <div className="bg-[var(--c-sunken)] rounded-lg p-4 border border-[var(--c-line)]">
                        <div className="text-3xl font-black text-cyan-700 dark:text-cyan-400">{Number(summary.overall_rating || 0).toFixed(2)}</div>
                        <div className="text-xs text-[var(--c-ink-2)] uppercase tracking-wider">Average Score</div>
                      </div>
                      <div className="bg-[var(--c-sunken)] rounded-lg p-4 border border-[var(--c-line)]">
                        <div className="text-3xl font-black text-amber-700 dark:text-amber-400">{summary.ratings_last_7_days}</div>
                        <div className="text-xs text-[var(--c-ink-2)] uppercase tracking-wider">Last 7 Days</div>
                      </div>
                      <div className="bg-[var(--c-sunken)] rounded-lg p-4 border border-[var(--c-line)]">
                        <div className="text-3xl font-black text-purple-600 dark:text-purple-400">{summary.ratings_last_30_days}</div>
                        <div className="text-xs text-[var(--c-ink-2)] uppercase tracking-wider">Last 30 Days</div>
                      </div>
                    </div>
                  </div>

                  {/* Case Management Section (if active case) */}
                  {activeCase && (
                    <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="text-2xl">🚨</div>
                        <div>
                          <div className="text-xl font-bold text-red-800 dark:text-red-200">Active Case</div>
                          <div className="text-sm text-red-700/70 dark:text-red-300/70">
                            {getCaseTypeLabel(activeCase.case_type)} • {activeCase.severity.toUpperCase()} severity
                          </div>
                        </div>
                      </div>

                      {/* Evidence Section */}
                      {caseEvidence.length > 0 && (
                        <div className="mb-4">
                          <div className="text-sm font-bold text-red-800 dark:text-red-200 mb-2">Evidence ({caseEvidence.length} reports):</div>
                          <div className="space-y-2 max-h-60 overflow-y-auto">
                            {caseEvidence.slice(0, 3).map(evidence => (
                              <div key={evidence.id} className="bg-[var(--c-surface)] rounded-lg p-3 border border-[var(--c-line)]">
                                <div className="flex gap-2 text-xs text-[var(--c-ink-2)] mb-1">
                                  <span>{evidence.rater_character_name}</span>
                                  <span>•</span>
                                  <span>{new Date(evidence.raid_date).toLocaleDateString()}</span>
                                </div>
                                <div className="flex gap-2">
                                  {evidence.conduct_rating && <span>{RATING_EMOJIS[evidence.conduct_rating]}</span>}
                                  {evidence.skill_rating && <span>{RATING_EMOJIS[evidence.skill_rating]}</span>}
                                  {evidence.timeliness_rating && <span>{RATING_EMOJIS[evidence.timeliness_rating]}</span>}
                                  {evidence.preparation_rating && <span>{RATING_EMOJIS[evidence.preparation_rating]}</span>}
                                </div>
                                {evidence.notes && (
                                  <div className="mt-2 text-sm text-[var(--c-ink)]">{evidence.notes}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Admin Action Form */}
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-bold text-red-800 dark:text-red-200 mb-1">Admin Notes:</label>
                          <textarea
                            value={adminNotes}
                            onChange={e => setAdminNotes(e.target.value)}
                            placeholder="Internal notes about investigation..."
                            rows={3}
                            className="w-full rounded-lg border border-red-500/30 bg-[var(--c-surface)] px-3 py-2 text-sm text-[var(--c-ink)] placeholder:text-[var(--c-ink-3)] outline-none focus:border-red-500/50 resize-none"
                          />
                          <button
                            onClick={updateCaseNotes}
                            className="mt-1 text-xs text-red-700 dark:text-red-300 hover:text-red-800 dark:hover:text-red-200 underline"
                          >
                            Save Notes
                          </button>
                        </div>

                        <div>
                          <label className="block text-sm font-bold text-red-800 dark:text-red-200 mb-1">Action Taken:</label>
                          <select
                            value={actionTaken}
                            onChange={e => setActionTaken(e.target.value)}
                            className="w-full rounded-lg border border-red-500/30 bg-[var(--c-surface)] px-3 py-2 text-sm text-[var(--c-ink)] outline-none focus:border-red-500/50"
                          >
                            <option value="">Select action...</option>
                            <option value="warning">⚠️ Warning Issued</option>
                            <option value="temporary_ban">🔒 Temporary Ban</option>
                            <option value="permanent_ban">🚫 Permanent Ban</option>
                            <option value="monitoring">👀 Under Monitoring</option>
                            <option value="contacted">📞 Player Contacted</option>
                            <option value="no_action">✅ No Action Needed</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-bold text-red-800 dark:text-red-200 mb-1">Resolution Notes:</label>
                          <textarea
                            value={resolutionNotes}
                            onChange={e => setResolutionNotes(e.target.value)}
                            placeholder="What was done to resolve this case?"
                            rows={2}
                            className="w-full rounded-lg border border-red-500/30 bg-[var(--c-surface)] px-3 py-2 text-sm text-[var(--c-ink)] placeholder:text-[var(--c-ink-3)] outline-none focus:border-red-500/50 resize-none"
                          />
                        </div>

                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={resolveCase}
                            disabled={resolvingCase || !actionTaken}
                            className="flex-1 px-4 py-3 rounded-lg bg-green-500/30 border border-green-500/50 text-green-800 dark:text-green-200 font-bold hover:bg-green-500/40 disabled:opacity-50 transition-all"
                          >
                            {resolvingCase ? 'Resolving...' : '✓ Resolve Case'}
                          </button>
                          <button
                            onClick={dismissCase}
                            className="px-4 py-3 rounded-lg bg-[var(--c-sunken-2)] border border-[var(--c-line-strong)] text-[var(--c-ink-2)] font-bold hover:bg-[var(--c-sunken-2)] transition-all"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rating Distribution */}
                  <div className="bg-gradient-to-br from-[var(--c-surface)] to-[var(--c-surface)] border border-[var(--c-line-strong)] rounded-xl p-6">
                    <h2 className="text-xl font-bold text-[var(--c-ink)] mb-6">Rating Distribution</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <RatingDistributionBar category="Conduct" distribution={summary.rating_distribution.conduct} />
                      <RatingDistributionBar category="Skill" distribution={summary.rating_distribution.skill} />
                      <RatingDistributionBar category="Timeliness" distribution={summary.rating_distribution.timeliness} />
                      <RatingDistributionBar category="Preparation" distribution={summary.rating_distribution.preparation} />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-[var(--c-line)]">
                      <div className="text-center">
                        <div className="text-4xl mb-2">{RATING_EMOJIS[Math.round(Number(summary.avg_conduct || 0))] || '😐'}</div>
                        <div className="text-2xl font-bold text-[var(--c-ink)]">{Number(summary.avg_conduct || 0).toFixed(2)}</div>
                        <div className="text-xs text-[var(--c-ink-2)] uppercase">Conduct</div>
                        <div className="text-xs text-[var(--c-ink-3)] mt-1">{summary.conduct_rating_count} ratings</div>
                      </div>
                      <div className="text-center">
                        <div className="text-4xl mb-2">{RATING_EMOJIS[Math.round(Number(summary.avg_skill || 0))] || '😐'}</div>
                        <div className="text-2xl font-bold text-[var(--c-ink)]">{Number(summary.avg_skill || 0).toFixed(2)}</div>
                        <div className="text-xs text-[var(--c-ink-2)] uppercase">Skill</div>
                        <div className="text-xs text-[var(--c-ink-3)] mt-1">{summary.skill_rating_count} ratings</div>
                      </div>
                      <div className="text-center">
                        <div className="text-4xl mb-2">{RATING_EMOJIS[Math.round(Number(summary.avg_timeliness || 0))] || '😐'}</div>
                        <div className="text-2xl font-bold text-[var(--c-ink)]">{Number(summary.avg_timeliness || 0).toFixed(2)}</div>
                        <div className="text-xs text-[var(--c-ink-2)] uppercase">Timeliness</div>
                        <div className="text-xs text-[var(--c-ink-3)] mt-1">{summary.timeliness_rating_count} ratings</div>
                      </div>
                      <div className="text-center">
                        <div className="text-4xl mb-2">{RATING_EMOJIS[Math.round(Number(summary.avg_preparation || 0))] || '😐'}</div>
                        <div className="text-2xl font-bold text-[var(--c-ink)]">{Number(summary.avg_preparation || 0).toFixed(2)}</div>
                        <div className="text-xs text-[var(--c-ink-2)] uppercase">Preparation</div>
                        <div className="text-xs text-[var(--c-ink-3)] mt-1">{summary.preparation_rating_count} ratings</div>
                      </div>
                    </div>
                  </div>

                  {/* All Ratings */}
                  {ratings.length > 0 && (
                    <div className="bg-gradient-to-br from-[var(--c-surface)] to-[var(--c-surface)] border border-[var(--c-line-strong)] rounded-xl p-6">
                      <h2 className="text-xl font-bold text-[var(--c-ink)] mb-6">All Reports ({ratings.length})</h2>
                      
                      <div className="space-y-4">
                        {ratings.map(rating => (
                          <div 
                            key={rating.id}
                            className={`rounded-xl border p-5 ${
                              rating.identity_concern || rating.has_poor_ratings
                                ? 'bg-red-500/5 border-red-500/30'
                                : 'bg-[var(--c-sunken)] border-[var(--c-line)]'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <div 
                                  className="w-10 h-10 rounded-full flex items-center justify-center text-[var(--c-ink)] font-bold text-xs"
                                  style={{ backgroundColor: getClassColor(rating.rater_class_name) }}
                                >
                                  {rating.rater_character_name.substring(0, 2).toUpperCase()}
                                </div>
                                <div>
                                  <div 
                                    className="font-bold"
                                    style={{ color: tc(getClassColor(rating.rater_class_name)) }}
                                  >
                                    {rating.rater_character_name}
                                  </div>
                                  <div className="text-xs text-[var(--c-ink-2)]">{rating.rater_user_name}</div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-xs text-[var(--c-ink-2)]">{rating.raid_name}</div>
                                <div className="text-xs text-[var(--c-ink-3)]">
                                  {new Date(rating.raid_date).toLocaleDateString()}
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-4 gap-3 mb-4">
                              {rating.conduct_rating && (
                                <div className="text-center p-3 rounded-lg bg-[var(--c-surface)]">
                                  <div className="text-3xl mb-1">{RATING_EMOJIS[rating.conduct_rating]}</div>
                                  <div className="text-xs text-[var(--c-ink-2)] uppercase">Conduct</div>
                                </div>
                              )}
                              {rating.skill_rating && (
                                <div className="text-center p-3 rounded-lg bg-[var(--c-surface)]">
                                  <div className="text-3xl mb-1">{RATING_EMOJIS[rating.skill_rating]}</div>
                                  <div className="text-xs text-[var(--c-ink-2)] uppercase">Skill</div>
                                </div>
                              )}
                              {rating.timeliness_rating && (
                                <div className="text-center p-3 rounded-lg bg-[var(--c-surface)]">
                                  <div className="text-3xl mb-1">{RATING_EMOJIS[rating.timeliness_rating]}</div>
                                  <div className="text-xs text-[var(--c-ink-2)] uppercase">Timeliness</div>
                                </div>
                              )}
                              {rating.preparation_rating && (
                                <div className="text-center p-3 rounded-lg bg-[var(--c-surface)]">
                                  <div className="text-3xl mb-1">{RATING_EMOJIS[rating.preparation_rating]}</div>
                                  <div className="text-xs text-[var(--c-ink-2)] uppercase">Preparation</div>
                                </div>
                              )}
                            </div>

                            {rating.identity_concern && (
                              <div className="mb-4 p-4 rounded-lg bg-red-500/20 border border-red-500/40">
                                <div className="font-bold text-red-700 dark:text-red-300 mb-2">⚠️ Identity Concern</div>
                                {rating.identity_concern_details && (
                                  <div className="text-sm text-red-800 dark:text-red-200">{rating.identity_concern_details}</div>
                                )}
                              </div>
                            )}

                            {rating.notes && (
                              <div className="mb-4 p-4 rounded-lg bg-[var(--c-sunken)] border border-[var(--c-line)]">
                                <div className="text-xs font-bold text-[var(--c-ink-2)] uppercase tracking-wider mb-2">Notes</div>
                                <div className="text-[var(--c-ink)] whitespace-pre-wrap">{rating.notes}</div>
                              </div>
                            )}

                            {rating.evidence_urls && rating.evidence_urls.length > 0 && (
                              <div>
                                <div className="text-xs font-bold text-[var(--c-ink-2)] uppercase tracking-wider mb-2">Evidence ({rating.evidence_urls.length})</div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                  {rating.evidence_urls.map((url, idx) => (
                                    <a
                                      key={idx}
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="relative aspect-video rounded-lg overflow-hidden border border-[var(--c-line-strong)] hover:border-cyan-500/50 transition-colors group"
                                    >
                                      <img 
                                        src={url} 
                                        alt={`Evidence ${idx + 1}`}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                                      />
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="mt-4 pt-4 border-t border-[var(--c-line)] text-xs text-[var(--c-ink-3)]">
                              Submitted {new Date(rating.created_at).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
